import moment from 'moment';
import _ from 'lodash';
import { useEffect } from 'react';

moment.locale('fi');

export const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

export function formatTime(time, detailed = false) {
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'days').startOf('day');
  const hours = moment(time).format('HH:mm');

  // is today or yesterday?
  if (moment(time).isSame(today, 'd')) return `Tänään ${hours}`;
  if (moment(time).isSame(yesterday, 'd')) return `Eilen ${hours}`;

  return detailed
    ? moment(time).format('DD.MM.YYYY HH:mm')
    : moment(time).format('DD.MM.YYYY');
}

export function fieldIsDateOrTimeField(inputType) {
  return inputType === 'time' || inputType === 'date';
}

const getTimeFromUtf = value => {
  if (!value) return '';
  return moment(value).local().format('HH:mm');
};

const getDateFromUtf = value => {
  if (!value) return '';
  return moment(value).format('YYYY-MM-DD');
};

export function getValueFromField(inputType, value) {
  if (fieldIsDateOrTimeField(inputType)) {
    if (inputType === 'time') {
      return getTimeFromUtf(value);
    }
    if (inputType === 'date') {
      return getDateFromUtf(value);
    }
    return null;
  }
  return value;
}

const getFormattedTimeFromUtf = value => {
  if (!value) return '-';
  return moment(value).format('HH:mm');
};

const getFormattedDateFromUtf = value => {
  if (!value) return '-';
  return moment(value).format('DD.MM.YYYY');
};

export function getFormattedValueFromField(inputType, value) {
  if (fieldIsDateOrTimeField(inputType)) {
    if (inputType === 'time') {
      return getFormattedTimeFromUtf(value);
    }
    if (inputType === 'date') {
      return getFormattedDateFromUtf(value);
    }
    return '';
  }
  return value;
}

export function getFieldValueToSave(value, type, savedTime) {
  if (type === 'date') {
    const reference = moment(savedTime);
    const date = moment(value);
    date.set({
      hour: reference.get('hour'),
      minute: reference.get('minute'),
    });
    return date.toISOString();
  }
  if (type === 'time') {
    const time = moment(value, 'HH:mm');
    const savedTimeValue = savedTime.value ? savedTime.value : savedTime;
    const reference = moment(savedTimeValue);
    reference.set({
      hour: time.get('hour'),
      minute: time.get('minute'),
    });
    return reference.toISOString();
  }
  return value;
}

export function isJSON(variableParam) {
  let variable = variableParam;
  if (typeof variable !== 'string') variable = JSON.stringify(variable);
  try {
    JSON.parse(variable);
    return true;
  } catch (e) {
    return false;
  }
}

export function formatCommentTime(time, showShortVersion) {
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'days').startOf('day');
  // is today or yesterday?
  if (moment(time).isSame(today, 'd') || moment(time).isSame(yesterday, 'd')) {
    return moment(time).calendar();
  }
  if (showShortVersion) return moment(time).format('DD.MM. HH.mm');
  return moment(time).format('DD.MM.YYYY HH.mm');
}

export function timeIsValid(val) {
  let isValid = true;
  const letterArr = val.split(':').join('').split('');
  const regexp = /^\d{0,2}?:?\d{0,2}$/;
  let valArr = [];

  if (!regexp.test(val)) {
    isValid = false;
  }
  // check each letter
  if (
    letterArr[0] &&
    (parseInt(letterArr[0], 10) < 0 || parseInt(letterArr[0], 10) > 2)
  ) {
    isValid = false;
  }

  if (
    letterArr[2] &&
    (parseInt(letterArr[2], 10) < 0 || parseInt(letterArr[2], 10) > 5)
  ) {
    isValid = false;
  }

  if (valArr.indexOf(':')) {
    valArr = val.split(':');
  } else {
    valArr.push(val);
  }
  // check mm and HH
  if (
    valArr[0] &&
    valArr[0].length &&
    (parseInt(valArr[0], 10) < 0 || parseInt(valArr[0], 10) > 23)
  ) {
    isValid = false;
  }

  if (
    valArr[1] &&
    valArr[1].length &&
    (parseInt(valArr[1], 10) < 0 || parseInt(valArr[1], 10) > 59)
  ) {
    isValid = false;
  }
  return isValid;
}

export function fieldsArrayToObject(arr) {
  const obj = {};
  if (!arr) return {};
  arr.forEach(field => {
    obj[field.name] = field;
  });
  return obj;
}

export const findFormByCustomContent = (customContent, navItems) => {
  const children = Object.values(navItems).map(category => category.children);
  const forms = _.flatten(children);
  const form = forms.find(f => f.customContent === customContent);
  return form;
};

export const findFormById = (id, navItems) => {
  const children = Object.values(navItems).map(category => category.children);
  const forms = _.flatten(children);
  const form = forms.find(f => f.formId === id);
  return form;
};

export const findFormByPathName = (path, navItems) => {
  // Get customContent from pathname
  const matchResult = path.match(
    /^\/palvelut\/([a-zäö]+)(?:\/muokkaa\/[0-9]+)?$/i
  );

  if (matchResult && matchResult.length) {
    const customContent = matchResult[1];

    return findFormByCustomContent(customContent, navItems);
  }

  // console.error(`findFormByCustomContent failed with input: ${path}`);
  return null;
};

export const getFormCustomContentByFormType = formType => {
  switch (formType) {
    case 'PROJECTS':
      return 'projektit';
    case 'BEDS':
      return 'sairaansijat';
    case 'CUSTOM_FORM_APPLIANCES':
      return 'laitehuolto';
    case 'CUSTOM_FORM_EXTENSIONS':
      return 'liittymahuolto';
    case 'CUSTOM_FORM_SONDS':
      return 'kutsujarjestelmahuolto';
    case 'CUSTOM_FORM_INTERNALPATIENTTRANSPORT':
      return 'potilaskuljetukset';
    case 'CUSTOM_FORM_SPACEREPAIRS':
      return 'tilakorjaukset';
    case 'CUSTOM_FORM_GASBOTTLES':
      return 'kaasupullot';
    case 'CUSTOM_FORM_INSTRUMENT_SERVICE':
      return 'valinehuolto';
    case 'CUSTOM_FORM_INTERNALPACKETTRANSPORT':
      return 'tavarakuljetukset';
    case 'CUSTOM_FORM_IT_SUPPORT_TICKETS':
      return 'itvika';
    case 'CUSTOM_FORM_WASTETRANSPORT':
      return 'jateastiat';
    case 'CUSTOM_FORM_PRODUCTS':
      return 'tuotteet';
    case 'CUSTOM_FORM_INFO_REQUEST_TICKETS':
      return 'tietopalvelupyynto';
    case 'CUSTOM_FORM_IT_SERVICE_LINKS':
      return 'ictlinkit';
    case 'CUSTOM_FORM_GENERIC_SUPPORT':
      return 'kayttotuki';
    case 'CUSTOM_FORM_CLEANING':
      return 'siivous';
    case 'CUSTOM_FORM_CLOTHING_MAINTENANCE':
      return 'teskstiilit';
    case 'CUSTOM_FORM_ACCESS_TAG_ORDER':
      return 'tunnistetilaus';
    case 'CUSTOM_FORM_ACCESS_TAG_ACTIVATION':
      return 'tunnisteaktivointi';
    case 'CUSTOM_FORM_DECEASED_TRANSPORT':
      return 'vainajakuljetus';
    case 'CUSTOM_FORM_EMAIL_GROUP_REQUEST':
      return 'ryhmapostilaatikot';
    case 'CUSTOM_FORM_WARDROBE_REQUEST':
      return 'pukukaappitilaus';
    case 'CUSTOM_FORM_BED_CLEAN':
      return 'sankypaikkasiivous';
    case 'CUSTOM_FORM_OPERATING_ROOM_CLEAN':
      return 'leikkaussalisiivous';
    case 'CUSTOM_FORM_EXAMINATION_ROOM_CLEAN':
      return 'tutkimushuonesiivous';
    case 'CUSTOM_FORM_SNACK':
      return 'valipala';
    case 'CUSTOM_FORM_DEVICE_LINKS':
      return 'laitelinkit';
    case 'CUSTOM_FORM_SPACEREQUEST':
      return 'tilapyynnot';
    default:
      console.error(
        `getFormCustomContentByFormType failed with input: ${formType}. Please add a case for "${formType}".`
      );
      return '';
  }
};

export const ScrollToTopOnMount = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return null;
};

export const topNavHeight = 70;

export const MAX_MOBILE_WIDTH = 767;
export const FLOATING_BUTTON_HEIGHT = 86;
